/* src/components/suggestions/SuggestionComposerModal.js */

import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

import {
  URGENCY_LEVELS,
  DEFAULT_URGENCY,
  createSuggestion,
  updateSuggestion,
} from "./suggestionsLogic";

import "./SuggestionComposerModal.css";

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 2000;

/**
 * Shared create/edit dialog.
 * - No `suggestion` prop  → "New Suggestion" (calls createSuggestion).
 * - `suggestion` passed in → "Edit Suggestion" (calls updateSuggestion).
 */
export default function SuggestionComposerModal({ suggestion, onClose, onSaved }) {
  const isEdit = !!suggestion;

  const [title, setTitle] = useState(suggestion?.title || "");
  const [description, setDescription] = useState(suggestion?.description || "");
  const [urgency, setUrgency] = useState(suggestion?.urgency || DEFAULT_URGENCY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Close on Escape — matches the other portal modals.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const cleanTitle = title.trim();
    const cleanDescription = description.trim();

    if (!cleanTitle) {
      setError("Please add a title.");
      return;
    }
    if (!cleanDescription) {
      setError("Please add a description.");
      return;
    }

    setError("");
    setSaving(true);
    try {
      if (isEdit) {
        await updateSuggestion(suggestion.id, {
          title: cleanTitle,
          description: cleanDescription,
          urgency,
        });
      } else {
        await createSuggestion({
          title: cleanTitle,
          description: cleanDescription,
          urgency,
        });
      }
      onSaved?.(isEdit ? "updated" : "created");
    } catch (err) {
      setError(
        err?.message ||
          (isEdit ? "Could not save the changes." : "Could not submit the suggestion.")
      );
      setSaving(false);
    }
  };

  return (
    <div className="sgModalBackdrop" onMouseDown={() => !saving && onClose()}>
      <div
        className="sgModal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit suggestion" : "New suggestion"}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sgModalHead">
          <div>
            <div className="sgModalTitle">
              {isEdit ? "Edit Suggestion" : "New Suggestion"}
            </div>
            <div className="sgModalSub">
              {isEdit
                ? "Update the details of this suggestion."
                : "Ideas, improvements and fixes are all welcome."}
            </div>
          </div>
          <button
            className="sgModalClose"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <FiX size={20} />
          </button>
        </div>

        <form className="sgModalBody" onSubmit={handleSubmit}>
          <label className="sgField">
            <span className="sgFieldLabel">Title</span>
            <input
              className="sgInput"
              value={title}
              maxLength={MAX_TITLE}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Add a filter to the payroll page"
              autoFocus
            />
          </label>

          <div className="sgField">
            <span className="sgFieldLabel">Urgency</span>
            <div className="sgUrgencyRow">
              {URGENCY_LEVELS.map((level) => (
                <button
                  type="button"
                  key={level.value}
                  className={`sgUrgencyOption sgUrgencyOption--${level.value} ${
                    urgency === level.value ? "active" : ""
                  }`}
                  onClick={() => setUrgency(level.value)}
                  aria-pressed={urgency === level.value}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          <label className="sgField">
            <span className="sgFieldLabel">Description</span>
            <textarea
              className="sgTextarea"
              value={description}
              maxLength={MAX_DESCRIPTION}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the idea, why it matters, and anything that would help us act on it."
              rows={6}
            />
            <span className="sgCharCount">
              {description.length}/{MAX_DESCRIPTION}
            </span>
          </label>

          {error && <div className="sgFormError">{error}</div>}

          <div className="sgModalFooter">
            <button
              type="button"
              className="sgBtn sgBtn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="sgBtn sgBtn--primary" disabled={saving}>
              {saving
                ? isEdit
                  ? "Saving…"
                  : "Submitting…"
                : isEdit
                ? "Save Changes"
                : "Submit Suggestion"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
